import {
  faCalendarStar,
  faCalendarClock,
} from '@fortawesome/pro-duotone-svg-icons';
import { Tooltip } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { Field } from 'api/tasks';
import { AddButton } from 'components/atoms/button';
import { EmptyState, EmptyStateIcon } from 'components/atoms/empty-state';
import { Tag } from 'components/atoms/tag';
import { RoutePaths } from 'constant';
import { useGetUserFromIndexedDb } from 'hooks/use-idb';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import {
  EmptyStateContainer,
  StyledText,
  DurationBetweenKeyEventsWrapper,
} from './styles';

export const EventEmptyState = (): JSX.Element => {
  return (
    <EmptyStateContainer>
      <EmptyState
        image={<EmptyStateIcon icon={faCalendarStar} />}
        title={<FormattedMessage id="settings.events.title" />}
        message={<FormattedMessage id="settings.events.empty_state" />}
        primaryCTA={
          <Link to={RoutePaths.newKeyevent}>
            <AddButton>
              <FormattedMessage id="settings.events.new" />
            </AddButton>
          </Link>
        }
      />
    </EmptyStateContainer>
  );
};

export const IntervalEmptyState = ({
  isCreateIntervalEnabled,
}: {
  isCreateIntervalEnabled: boolean;
}): JSX.Element => {
  const { pathname } = useLocation();

  return (
    <EmptyStateContainer>
      <EmptyState
        image={<EmptyStateIcon icon={faCalendarClock} />}
        title={<FormattedMessage id="settings.intervals.title" />}
        message={<FormattedMessage id="settings.intervals.empty_state" />}
        primaryCTA={
          <Link
            to={!isCreateIntervalEnabled ? pathname : RoutePaths.newInterval}
          >
            <Tooltip
              title={
                isCreateIntervalEnabled ? (
                  <FormattedMessage id="settings.intervals.disabled.message" />
                ) : (
                  ''
                )
              }
              placement="top"
            >
              <div>
                <AddButton>
                  <FormattedMessage id="settings.intervals.new" />
                </AddButton>
              </div>
            </Tooltip>
          </Link>
        }
      />
    </EmptyStateContainer>
  );
};

export const DurationBetweenKeyEventsRenderer = ({
  interval,
  keyEvents,
}: {
  interval: Field;
  keyEvents: Field[];
}): JSX.Element | null => {
  const startKeyEventId = interval?.metaData?.startKeyEventId ?? 0;
  const endKeyEventId = interval?.metaData?.endKeyEventId ?? 0;

  if (!startKeyEventId && !endKeyEventId) {
    return null;
  }

  const startKeyEventLabel = keyEvents?.find(
    (field) => field.fieldId === startKeyEventId,
  )?.fieldLabel;
  const endKeyEventLabel = keyEvents?.find(
    (field) => field.fieldId === endKeyEventId,
  )?.fieldLabel;

  if (startKeyEventLabel && endKeyEventLabel) {
    return (
      <DurationBetweenKeyEventsWrapper>
        <Tag color={getRandomColor(startKeyEventId)}>{startKeyEventLabel}</Tag>
        <Tag color={getRandomColor(endKeyEventId)}>{endKeyEventLabel}</Tag>
      </DurationBetweenKeyEventsWrapper>
    );
  }

  return <>-</>;
};

export const TextWithTooltip = ({
  text,
  dataCy,
}: {
  text: string;
  dataCy: string;
}): JSX.Element => {
  return (
    <StyledText data-cy={dataCy} ellipsis={{ tooltip: true }}>
      {text}
    </StyledText>
  );
};

export const GetNameElement = ({ id }: { id: number | undefined }) => {
  const user = useGetUserFromIndexedDb(id);

  const name = fullName(user);

  return <span data-cy={`key-event.get-name.${name}`}>{name}</span>;
};

export const UserNameWithTooltip = ({
  id,
}: {
  id: number | undefined;
}): JSX.Element => {
  return (
    <StyledText ellipsis={{ tooltip: true }}>
      <GetNameElement id={id} />
    </StyledText>
  );
};
